/**
 * "Bottom feature cards = video chapters" for the generic media section (sections/soulog-media-feature.liquid) (no dependencies).
 *
 * Requirement (2026-09-17): the video in the product page's MAGNETIC DESIGN segment actually demonstrates four highlights, matching the four cards below one to one,
 * but the cards were purely presentational — a user wanting to see a given highlight had to watch the video and wait for the loop to come around. Now:
 *
 *   · Click a card → the video seeks to that card's start second and keeps playing from there (it does not stop at the end of the segment).
 *   · Whichever segment the video is playing → that card highlights automatically, so the "card ↔ clip" mapping is visible even without clicking.
 *
 * Time points come from each card's "Video start time (seconds)" setting in the theme editor (video_start in the schema),
 * so swapping video footage needs no code change; if none are filled in, Liquid doesn't add smf__features--chapters and this whole file is a no-op.
 *
 * ## Two pitfalls that must be handled
 *
 * 1. **Seeking before metadata**. Apart from the first screen, this section's videos are lazy-loaded (preload="none", see
 *    assets/soulog-media-feature.js); assigning currentTime while readyState is 0 is simply discarded —
 *    clicking a card makes the video play from the start, which looks like "nothing happened". So when not ready, record the target time first and apply it on loadedmetadata.
 * 2. **Don't race the lazy-load script over load()**. soulog-media-feature.js's warm() uses dataset.smfWarm as its idempotency flag;
 *    calling load() again on a video that's already playing resets progress to 0. So when we raise preload ourselves we claim that flag at the same time,
 *    so it doesn't load() once more afterwards and wipe out the position the user just jumped to.
 *
 * Regression guard: tests/unit/soulog-media-chapters.test.mjs
 */
(function () {
	"use strict";

	// Multiple media sections on the same page each include this file; the browser downloads it once but executes it multiple times.
	if (window.__soulogMediaChapters) return;
	window.__soulogMediaChapters = true;

	var ROW = ".smf__features--chapters";
	var HIT = ".smf__feature-hit";
	var CARD = ".smf__feature";
	var ACTIVE = "smf__feature--active";
	// Tolerance for the highlight check: timeupdate's currentTime is often tens of milliseconds off the chapter start,
	// and without tolerance the previous segment would be highlighted in the instant right after a seek.
	var EPS = 0.08;

	function starts(row) {
		var hits = row.querySelectorAll(HIT);
		var list = [];
		for (var i = 0; i < hits.length; i++) {
			var t = parseFloat(hits[i].dataset.smfStart);
			if (isNaN(t) || t < 0) continue;
			list.push({ hit: hits[i], start: t });
		}
		return list;
	}

	/** Which segment the current time falls in: among those with start ≤ currentTime, take the one with the largest start (does not assume DOM order = time order) */
	function activeIndex(list, t) {
		var best = -1;
		var bestStart = -1;
		for (var i = 0; i < list.length; i++) {
			var s = list[i].start;
			if (s <= t + EPS && s >= bestStart) {
				best = i;
				bestStart = s;
			}
		}
		return best;
	}

	function paint(list, index) {
		for (var i = 0; i < list.length; i++) {
			var card = list[i].hit.closest(CARD) || list[i].hit.parentNode;
			if (i === index) {
				if (card && card.classList) card.classList.add(ACTIVE);
				list[i].hit.setAttribute("aria-current", "true");
			} else {
				if (card && card.classList) card.classList.remove(ACTIVE);
				list[i].hit.removeAttribute("aria-current");
			}
		}
	}

	function play(video) {
		var r = video.play();
		if (r && r.catch) r.catch(function () {});
	}

	/**
	 * Jump to the given second and keep playing from there.
	 * When not ready (readyState < 1 = no metadata), assigning currentTime has no effect,
	 * so we can only raise preload to trigger the download first, then actually seek on loadedmetadata.
	 */
	function seek(video, t) {
		if (video.readyState < 1) {
			video.dataset.smfChapterSeek = String(t);
			// Claim the lazy-load script's idempotency flag so it doesn't load() again later and wipe out the position we jumped to
			video.dataset.smfWarm = "1";
			video.preload = "auto";
			if (!video.dataset.smfChapterWait) {
				video.dataset.smfChapterWait = "1";
				video.addEventListener("loadedmetadata", function () {
					var pending = parseFloat(video.dataset.smfChapterSeek);
					if (!isNaN(pending)) video.currentTime = pending;
					play(video);
				});
			}
			if (video.readyState === 0) video.load();
			return;
		}
		video.currentTime = t;
		play(video);
	}

	function bind(row) {
		if (row.dataset.smfChapters) return;
		var section = row.closest(".smf");
		var video = section ? section.querySelector("video") : null;
		// With no video to jump in, treat this row of cards as not chapters: no click binding and no highlighting (Liquid already avoids adding the class where possible; this is the fallback)
		if (!video) return;
		var list = starts(row);
		if (!list.length) return;
		row.dataset.smfChapters = "1";

		row.addEventListener("click", function (e) {
			var hit = e.target && e.target.closest ? e.target.closest(HIT) : null;
			if (!hit) return;
			var t = parseFloat(hit.dataset.smfStart);
			if (isNaN(t)) return;
			seek(video, t);
			// Highlight immediately from the click, without waiting for timeupdate — a lazy-loaded video may take a few hundred milliseconds to fire its first timeupdate
			for (var i = 0; i < list.length; i++) {
				if (list[i].hit === hit) paint(list, i);
			}
		});

		function sync() {
			paint(list, activeIndex(list, video.currentTime || 0));
		}
		video.addEventListener("timeupdate", sync);
		video.addEventListener("seeked", sync);
		video.addEventListener("play", sync);
		sync();
	}

	function apply(root) {
		var scope = root && root.querySelectorAll ? root : document;
		var rows = scope.querySelectorAll(ROW);
		for (var i = 0; i < rows.length; i++) bind(rows[i]);
	}

	apply(document);

	// Adding/removing / re-rendering sections in the theme editor re-mounts the DOM
	document.addEventListener("shopify:section:load", function (e) {
		apply(e.target || document);
	});
})();
